// Cliente HTTP del frontend. Centraliza la URL base (del entorno, no hardcodeada)
// y adjunta el token JWT en cada llamada. Así los componentes no repiten fetch.

const API = process.env.NEXT_PUBLIC_API_URL ?? 'https://demo-api-disciplinario.tecnovapp.com.co/api/v1';

export function getToken(): string | null {
  return typeof window !== 'undefined' ? localStorage.getItem('token') : null;
}

/**
 * Cierra la sesión cuando el token expiró o dejó de ser válido (401 con token).
 * Limpia el almacenamiento, deja una marca para avisar en el login y redirige.
 * Evita que un token vencido se vea como un "error al guardar" confuso.
 */
function expirarSesion(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('token');
  localStorage.removeItem('usuario');
  localStorage.setItem('sesion_expirada', '1');
  // Redirige a la raíz (login). replace() evita volver atrás a una vista sin sesión.
  window.location.replace('/');
}

interface Opciones {
  method?: string;
  body?: unknown;
  auth?: boolean; // por defecto true: adjunta el token si existe
}

export async function api<T = unknown>(path: string, opts: Opciones = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = opts;
  const token = auth ? getToken() : null;

  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // Sesión expirada/ inválida (solo si se envió token): cerrar sesión y volver al login.
    if (res.status === 401 && token) {
      expirarSesion();
      throw new Error('Su sesión expiró. Ingrese de nuevo.');
    }
    // El backend devuelve message como string o array (validación).
    const m = data?.message;
    throw new Error(Array.isArray(m) ? m.join(' · ') : (m ?? `Error ${res.status}`));
  }
  return data as T;
}

/** Subida multipart (archivos). No fija Content-Type: el navegador pone el boundary. */
export async function subir<T = unknown>(path: string, form: FormData): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && token) {
      expirarSesion();
      throw new Error('Su sesión expiró. Ingrese de nuevo.');
    }
    const m = data?.message;
    throw new Error(Array.isArray(m) ? m.join(' · ') : (m ?? `Error ${res.status}`));
  }
  return data as T;
}

/** Descarga autenticada de un binario (PDF) como Blob, para abrirlo en el navegador. */
export async function descargarBlob(path: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    if (res.status === 401 && token) {
      expirarSesion();
      throw new Error('Su sesión expiró. Ingrese de nuevo.');
    }
    throw new Error('No se pudo abrir el documento.');
  }
  return res.blob();
}
